// src/components/auto/AutoTable.jsx - Enhanced with mobile card layout + archive + viewer role
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { AlertTriangle, Calendar, Car, Edit, MapPin, StickyNote, Trash2, User } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function AutoTable({
  auto,
  getClienteName,
  getStatoName,
  getCampataFullName,
  onEdit,
  onDelete,
  isLoading,
  deleteLoading,
  stati = [],
  isViewer = false,
  isMobile = false
}) {
  const navigate = useNavigate();
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    auto: null,
    step: 1 // 1 = first confirmation, 2 = second confirmation
  });

  // Format date for display (DD/MM/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "N/D";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/D";
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error("Error formatting date for display:", error);
      return "N/D";
    }
  };

  // Check if auto can be deleted (status is "Chiusa")
  const canDeleteAuto = (autoItem) => {
    const stato = stati.find(s => s.id === autoItem.stato_id);
    return stato && stato.nome.toLowerCase() === "chiusa";
  };

  const handleDeleteClick = (autoItem) => {
    setDeleteConfirmation({
      open: true,
      auto: autoItem,
      step: 1
    });
  };

  const handleFirstConfirmation = () => {
    setDeleteConfirmation(prev => ({
      ...prev,
      step: 2
    }));
  };

  const handleFinalDelete = () => {
    if (deleteConfirmation.auto) {
      onDelete(deleteConfirmation.auto);
    }
    setDeleteConfirmation({
      open: false,
      auto: null,
      step: 1
    });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({
      open: false,
      auto: null,
      step: 1
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Caricamento auto...</p>
        </div>
      </div>
    );
  }

  if (auto.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="p-8 text-center">
          <Car className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">Nessuna auto trovata</h3>
          <p className="text-slate-500">
            {isViewer ? "Nessuna auto trovata con i filtri applicati." : "Aggiungi la prima auto o modifica i filtri di ricerca."}
          </p>
        </div>
      </div>
    );
  }

  // ──────── MOBILE CARD LAYOUT ────────
  const renderMobileCards = () => (
    <div className="space-y-3">
      {auto.map((autoItem, index) => (
        <motion.div
          key={autoItem.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden"
        >
          {/* Card Header: Targa + Actions */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
            <div className="flex items-center gap-2 min-w-0">
              <Car className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="font-bold text-slate-800 text-base truncate">
                {autoItem.targa}
              </span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex-shrink-0">
                {getStatoName(autoItem.stato_id)}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {autoItem.note && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-blue-50"
                  onClick={() => navigate(`/auto/note/${autoItem.id}`)}
                  title="Visualizza nota"
                >
                  <StickyNote className="w-4 h-4 text-blue-500" />
                </Button>
              )}
              {!isViewer && onEdit && (
                <Button
                  onClick={() => onEdit(autoItem)}
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-blue-50 hover:text-blue-700"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              )}
              {!isViewer && onDelete && canDeleteAuto(autoItem) && (
                <Button
                  onClick={() => handleDeleteClick(autoItem)}
                  variant="ghost"
                  size="sm"
                  disabled={deleteLoading}
                  className="h-9 w-9 p-0 hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Card Body: Info Grid */}
          <div className="px-4 py-3">
            {/* Marca / Modello */}
            <p className="text-sm text-slate-700 mb-2">
              {autoItem.marca} {autoItem.modello}
              {autoItem.numero_telaio && (
                <span className="text-xs text-slate-400 ml-2">
                  ({autoItem.numero_telaio})
                </span>
              )}
            </p>

            {/* 2-column info grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              <div className="flex items-center gap-1.5 text-slate-500">
                <Calendar className="w-3 h-3 text-green-600 flex-shrink-0" />
                <span>{formatDateForDisplay(autoItem.data_ingresso)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-slate-500">
                <User className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{getClienteName(autoItem.cliente_id)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-slate-500">
                <Calendar className={`w-3 h-3 flex-shrink-0 ${autoItem.data_uscita ? 'text-red-600' : 'text-slate-300'}`} />
                <span>{autoItem.data_uscita ? formatDateForDisplay(autoItem.data_uscita) : "N/D"}</span>
              </div>
            </div>

            {/* Position - full width row */}
            <div className="flex items-center gap-1.5 text-xs text-slate-600 mt-2 pt-2 border-t border-slate-100">
              <MapPin className="w-3 h-3 text-blue-500 flex-shrink-0" />
              <span>{getCampataFullName(autoItem.campata_id)}</span>
            </div>
          </div>
        </motion.div>
      ))}

      {/* Footer */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-3">
        <p className="text-xs text-center text-slate-500">
          Totale: <span className="font-semibold text-slate-700">{auto.length}</span> auto
        </p>
      </div>
    </div>
  );

  // ──────── DESKTOP TABLE LAYOUT ────────
  const renderDesktopTable = () => (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left p-4 font-medium text-slate-700">
                <div className="flex items-center space-x-2">
                  <Car className="w-4 h-4" />
                  <span>Auto</span>
                </div>
              </th>
              <th className="text-left p-4 font-medium text-slate-700">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>Cliente</span>
                </div>
              </th>
              <th className="text-left p-4 font-medium text-slate-700">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>Data Ingresso</span>
                </div>
              </th>
              <th className="text-left p-4 font-medium text-slate-700">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>Data Uscita</span>
                </div>
              </th>
              <th className="text-left p-4 font-medium text-slate-700">Stato</th>
              <th className="text-left p-4 font-medium text-slate-700">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Posizione</span>
                </div>
              </th>
              <th className="text-center p-4 font-medium text-slate-700 w-12">
                <div className="flex items-center justify-center">
                  <StickyNote className="w-4 h-4" />
                </div>
              </th>
              {!isViewer && (
                <th className="text-left p-4 font-medium text-slate-700">Azioni</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {auto.map((autoItem, index) => (
              <motion.tr
                key={autoItem.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="hover:bg-slate-50 transition-colors"
              >
                <td className="p-4">
                  <div className="space-y-1">
                    <div className="font-semibold text-slate-800">
                      {autoItem.targa}
                    </div>
                    <div className="text-sm text-slate-600">
                      {autoItem.marca} {autoItem.modello}
                    </div>
                    {autoItem.numero_telaio && (
                      <div className="text-xs text-slate-500">
                        Telaio: {autoItem.numero_telaio}
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-slate-800">
                    {getClienteName(autoItem.cliente_id)}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-green-600" />
                    <span className="text-slate-800">
                      {formatDateForDisplay(autoItem.data_ingresso)}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className={`w-4 h-4 ${autoItem.data_uscita ? 'text-red-600' : 'text-slate-400'}`} />
                    <span className={autoItem.data_uscita ? 'text-slate-800' : 'text-slate-500'}>
                      {autoItem.data_uscita ? formatDateForDisplay(autoItem.data_uscita) : "N/D"}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm text-slate-800 font-medium">
                    {getStatoName(autoItem.stato_id)}
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm text-slate-600">
                    {getCampataFullName(autoItem.campata_id)}
                  </div>
                </td>
                <td className="p-4 text-center">
                  {autoItem.note && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                      onClick={() => navigate(`/auto/note/${autoItem.id}`)}
                      title="Visualizza nota"
                    >
                      <StickyNote className="w-4 h-4 text-blue-500" />
                    </Button>
                  )}
                </td>
                {!isViewer && (
                  <td className="p-4">
                    <div className="flex space-x-2">
                      {onEdit && (
                        <Button
                          onClick={() => onEdit(autoItem)}
                          variant="outline"
                          size="sm"
                          className="hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {onDelete && canDeleteAuto(autoItem) && (
                        <Button
                          onClick={() => handleDeleteClick(autoItem)}
                          variant="outline"
                          size="sm"
                          disabled={deleteLoading}
                          className="hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-slate-50 px-4 py-3 border-t border-slate-200">
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>Totale: {auto.length} auto</span>
          <span>Filtrate: {auto.length} risultati</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? renderMobileCards() : renderDesktopTable()}

      {/* Double Confirmation Dialog - Only show if not viewer */}
      {!isViewer && (
        <Dialog open={deleteConfirmation.open} onOpenChange={handleCancelDelete}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                {deleteConfirmation.step === 1 ? "Conferma eliminazione" : "Conferma definitiva"}
              </DialogTitle>
              <DialogDescription>
                {deleteConfirmation.step === 1 ? (
                  <>
                    Stai per archiviare ed eliminare l'auto <strong>{deleteConfirmation.auto?.targa}</strong>
                    ({deleteConfirmation.auto?.marca} {deleteConfirmation.auto?.modello}).
                    <br /><br />
                    Il record verrà spostato nell'archivio prima dell'eliminazione.
                    <br /><br />
                    Sei sicuro di voler continuare?
                  </>
                ) : (
                  <>
                    <strong>ATTENZIONE:</strong> Questa è l'ultima conferma!
                    <br /><br />
                    L'auto <strong>{deleteConfirmation.auto?.targa}</strong> verrà definitivamente
                    archiviata e rimossa dalla gestione attiva.
                    <br /><br />
                    Questa azione non può essere annullata. Continuare?
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCancelDelete}>
                Annulla
              </Button>
              {deleteConfirmation.step === 1 ? (
                <Button variant="destructive" onClick={handleFirstConfirmation}>
                  Continua
                </Button>
              ) : (
                <Button
                  variant="destructive"
                  onClick={handleFinalDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Archiviando..." : "Elimina definitivamente"}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Note Read-Only Modal removed - using dedicated page now */}
    </>
  );
}